/**
 * Hala CRM - Request Details Page
 * Full request information with notes, activity log, and role-based actions
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, User, Phone, Mail, Calendar, Clock, FileText, MessageSquare,
  Plus, Send, CheckCircle, XCircle, Play, Pause, Undo2, Users, History,
  AlertCircle, Inbox, UserCheck, RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = {
  primary: '#1B3A6B',
  gold: '#C5A572',
  success: '#10B981',
  successLight: '#D1FAE5',
  warning: '#F59E0B',
  warningLight: '#FEF3C7',
  error: '#EF4444',
  errorLight: '#FEE2E2',
  info: '#3B82F6',
  infoLight: '#DBEAFE',
  white: '#FFFFFF',
  bg: '#F8FAFC',
  bgDark: '#F1F5F9',
  border: '#E2E8F0',
  text: '#1E293B',
  textMuted: '#64748B',
};

const STATUS_CONFIG = {
  NEW: { label: 'جديد', color: COLORS.info, bg: COLORS.infoLight, icon: FileText },
  IN_POOL: { label: 'في المجمع', color: COLORS.warning, bg: COLORS.warningLight, icon: Inbox },
  ASSIGNED: { label: 'مُسند', color: COLORS.primary, bg: `${COLORS.primary}20`, icon: UserCheck },
  IN_PROGRESS: { label: 'قيد التنفيذ', color: COLORS.gold, bg: `${COLORS.gold}20`, icon: Clock },
  COMPLETED: { label: 'مكتمل', color: COLORS.success, bg: COLORS.successLight, icon: CheckCircle },
  CANCELLED: { label: 'ملغي', color: COLORS.error, bg: COLORS.errorLight, icon: XCircle },
};

const ACTIVITY_ICONS = {
  STATUS_CHANGE: Clock,
  CLAIMED: UserCheck,
  UNCLAIMED: Undo2,
  ASSIGNED: Users,
  NOTE_ADDED: MessageSquare,
  CREATED: Plus,
};

function StatusBadge({ status, size = 'normal' }) {
  const config = STATUS_CONFIG[status] || { label: status, color: COLORS.textMuted, bg: COLORS.bgDark, icon: AlertCircle };
  const Icon = config.icon;
  const isSmall = size === 'small';
  
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: isSmall ? '4px' : '6px',
      padding: isSmall ? '4px 10px' : '6px 14px',
      borderRadius: '20px',
      backgroundColor: config.bg,
      color: config.color,
      fontSize: isSmall ? '12px' : '14px',
      fontWeight: 500,
    }}>
      <Icon size={isSmall ? 14 : 16} />
      {config.label}
    </span>
  );
}

export default function CRMRequestDetailsPage() {
  const { requestId } = useParams();
  const { user, getCRMHeaders } = useOutletContext();
  const navigate = useNavigate();
  
  const [request, setRequest] = useState(null);
  const [notes, setNotes] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [activeTab, setActiveTab] = useState('notes'); // notes, activity

  const fetchDetails = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    
    try {
      const [detailsRes, notesRes, activityRes] = await Promise.all([
        axios.get(`${API}/requests/${requestId}/details`, { headers: getCRMHeaders() }),
        axios.get(`${API}/requests/${requestId}/notes`, { headers: getCRMHeaders() }),
        axios.get(`${API}/requests/${requestId}/activity`, { headers: getCRMHeaders() }),
      ]);
      
      setRequest(detailsRes.data);
      setNotes(notesRes.data || []);
      setActivity(activityRes.data || []);
    } catch (e) {
      console.error('Failed to fetch details:', e);
    } finally {
      setLoading(false);
    }
  }, [requestId, getCRMHeaders]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    
    try {
      await axios.post(
        `${API}/requests/${requestId}/notes`,
        { note: newNote },
        { headers: getCRMHeaders() }
      );
      setNewNote('');
      fetchDetails();
    } catch (e) {
      console.error('Failed to add note:', e);
      alert('فشل إضافة الملاحظة');
    } finally {
      setAddingNote(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setActionLoading(newStatus);
    
    try {
      await axios.put(
        `${API}/requests/${requestId}/status`,
        { status: newStatus },
        { headers: getCRMHeaders() }
      );
      fetchDetails();
    } catch (e) {
      console.error('Failed to change status:', e);
      alert(e.response?.data?.detail || 'فشل تغيير الحالة');
    } finally {
      setActionLoading(null);
    }
  };

  const handleClaim = async () => {
    setActionLoading('claim');
    
    try {
      await axios.post(
        `${API}/requests/${requestId}/claim`,
        { user_id: user.user_id },
        { headers: getCRMHeaders() }
      );
      fetchDetails();
    } catch (e) {
      console.error('Failed to claim:', e);
      alert(e.response?.data?.detail || 'فشل المطالبة بالطلب');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnclaim = async () => {
    setActionLoading('unclaim');
    
    try {
      await axios.post(
        `${API}/requests/${requestId}/unclaim`,
        { user_id: user.user_id },
        { headers: getCRMHeaders() }
      );
      fetchDetails();
    } catch (e) {
      console.error('Failed to unclaim:', e);
      alert(e.response?.data?.detail || 'فشل إلغاء المطالبة');
    } finally {
      setActionLoading(null);
    }
  };

  // Determine available actions based on role and status
  const getAvailableActions = () => {
    if (!request || !user) return [];
    
    const actions = [];
    const role = user.role_id;
    const status = request.status;
    const isOwner = request.current_owner_id === user.user_id;
    
    // Claim action for pool requests
    // Note: Consultants CANNOT claim - they receive direct assignments only
    if (status === 'IN_POOL' && (role === 'group_user' || role === 'admin' || role === 'supervisor')) {
      actions.push({ key: 'claim', label: 'المطالبة بالطلب', icon: UserCheck, color: COLORS.primary, onClick: handleClaim });
    }
    
    // Unclaim - only owner can unclaim, and only group_user/supervisor/admin
    // Consultants CANNOT unclaim because they don't use the pool model
    if (isOwner && (status === 'ASSIGNED' || status === 'IN_PROGRESS') && role !== 'consultant') {
      actions.push({ key: 'unclaim', label: 'إعادة للمجمع', icon: Undo2, color: COLORS.warning, onClick: handleUnclaim });
    }
    
    // Status transitions for owner
    if (isOwner) {
      if (status === 'ASSIGNED') {
        actions.push({ key: 'IN_PROGRESS', label: 'بدء العمل', icon: Play, color: COLORS.gold, onClick: () => handleStatusChange('IN_PROGRESS') });
      }
      if (status === 'IN_PROGRESS') {
        actions.push({ key: 'COMPLETED', label: 'إكمال الطلب', icon: CheckCircle, color: COLORS.success, onClick: () => handleStatusChange('COMPLETED') });
        actions.push({ key: 'ASSIGNED', label: 'إيقاف مؤقت', icon: Pause, color: COLORS.textMuted, onClick: () => handleStatusChange('ASSIGNED') });
      }
      if (['ASSIGNED', 'IN_PROGRESS'].includes(status)) {
        actions.push({ key: 'CANCELLED', label: 'إلغاء الطلب', icon: XCircle, color: COLORS.error, onClick: () => handleStatusChange('CANCELLED') });
      }
    }
    
    // Admin/Supervisor can reopen completed requests
    if ((role === 'admin' || role === 'supervisor') && status === 'COMPLETED') {
      actions.push({ key: 'IN_PROGRESS', label: 'إعادة فتح', icon: RefreshCw, color: COLORS.info, onClick: () => handleStatusChange('IN_PROGRESS') });
    }
    
    return actions;
  };

  if (loading) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: COLORS.textMuted }}>
        جاري التحميل...
      </div>
    );
  }

  if (!request) {
    return (
      <div style={{ padding: '60px', textAlign: 'center' }}>
        <AlertCircle size={48} style={{ marginBottom: '16px', opacity: 0.4 }} color={COLORS.error} />
        <p style={{ margin: 0, color: COLORS.textMuted }}>الطلب غير موجود</p>
      </div>
    );
  }

  const actions = getAvailableActions();

  return (
    <div style={{ direction: 'rtl' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            border: 'none',
            backgroundColor: 'transparent',
            color: COLORS.textMuted,
            fontSize: '14px',
            cursor: 'pointer',
            marginBottom: '16px',
          }}
        >
          <ArrowLeft size={18} />
          رجوع
        </button>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: COLORS.text }}>
                تفاصيل الطلب
              </h1>
              <StatusBadge status={request.status} />
            </div>
            <p style={{ margin: 0, fontSize: '14px', color: COLORS.textMuted }}>
              رقم الطلب: {request.request_id}
            </p>
          </div>
          
          {/* Actions */}
          {actions.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {actions.map((action) => {
                const Icon = action.icon;
                const isLoading = actionLoading === action.key;
                return (
                  <button
                    key={action.key}
                    onClick={action.onClick}
                    disabled={!!actionLoading}
                    data-testid={`action-${action.key}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '10px 16px',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: action.color,
                      color: COLORS.white,
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: actionLoading ? 'not-allowed' : 'pointer',
                      opacity: actionLoading ? 0.7 : 1,
                      transition: 'all 0.2s',
                    }}
                  >
                    {isLoading ? '...' : <Icon size={16} />}
                    {action.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '24px' }}>
        {/* Left Column - Request Info */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Applicant Card */}
          <div style={{
            backgroundColor: COLORS.white,
            borderRadius: '12px',
            border: `1px solid ${COLORS.border}`,
            padding: '20px',
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
              معلومات مقدم الطلب
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: COLORS.bgDark,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <User size={20} color={COLORS.textMuted} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>الاسم</p>
                  <p style={{ margin: '2px 0 0', fontSize: '14px', fontWeight: 500, color: COLORS.text }}>
                    {request.applicant_name || '-'}
                  </p>
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: COLORS.bgDark,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Phone size={20} color={COLORS.textMuted} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>الجوال</p>
                  <p style={{ margin: '2px 0 0', fontSize: '14px', fontWeight: 500, color: COLORS.text, direction: 'ltr' }}>
                    {request.mobile || '-'}
                  </p>
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: COLORS.bgDark,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Mail size={20} color={COLORS.textMuted} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>البريد</p>
                  <p style={{ margin: '2px 0 0', fontSize: '14px', fontWeight: 500, color: COLORS.text }}>
                    {request.email || '-'}
                  </p>
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: COLORS.bgDark,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Calendar size={20} color={COLORS.textMuted} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>تاريخ الإنشاء</p>
                  <p style={{ margin: '2px 0 0', fontSize: '14px', fontWeight: 500, color: COLORS.text }}>
                    {formatDate(request.created_at)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Service Info Card */}
          <div style={{
            backgroundColor: COLORS.white,
            borderRadius: '12px',
            border: `1px solid ${COLORS.border}`,
            padding: '20px',
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
              معلومات الخدمة
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>الخدمة</p>
                <p style={{ margin: '4px 0 0', fontSize: '15px', fontWeight: 500, color: COLORS.text }}>
                  {request.service_name_ar || '-'}
                </p>
              </div>
              <div>
                <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>البرنامج</p>
                <p style={{ margin: '4px 0 0', fontSize: '15px', fontWeight: 500, color: COLORS.text }}>
                  {request.program_name_ar || '-'}
                </p>
              </div>
              <div>
                <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>نوع المعالجة</p>
                <span style={{
                  display: 'inline-block',
                  marginTop: '4px',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  backgroundColor: request.handling_type === 'CONSULTANT' ? `${COLORS.gold}20` : `${COLORS.info}20`,
                  color: request.handling_type === 'CONSULTANT' ? COLORS.gold : COLORS.info,
                  fontSize: '13px',
                  fontWeight: 500,
                }}>
                  {request.handling_type === 'CONSULTANT' ? 'مستشار مباشر' : 'مجمع المجموعة'}
                </span>
              </div>
              <div>
                <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>المصدر</p>
                <p style={{ margin: '4px 0 0', fontSize: '15px', fontWeight: 500, color: COLORS.text }}>
                  {request.source === 'HALA' ? 'محادثة هلا' : request.source || '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Assignment Info Card */}
          <div style={{
            backgroundColor: COLORS.white,
            borderRadius: '12px',
            border: `1px solid ${COLORS.border}`,
            padding: '20px',
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
              معلومات الإسناد
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>المالك الحالي</p>
                <p style={{ margin: '4px 0 0', fontSize: '15px', fontWeight: 500, color: COLORS.text }}>
                  {request.current_owner_name || (request.current_owner_type === 'GROUP' ? 'مجمع المجموعة' : '-')}
                </p>
              </div>
              <div>
                <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>المجموعة</p>
                <p style={{ margin: '4px 0 0', fontSize: '15px', fontWeight: 500, color: COLORS.text }}>
                  {request.group_name_ar || '-'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Notes & Activity */}
        <div style={{
          backgroundColor: COLORS.white,
          borderRadius: '12px',
          border: `1px solid ${COLORS.border}`,
          display: 'flex',
          flexDirection: 'column',
          height: 'fit-content',
          maxHeight: 'calc(100vh - 200px)',
        }}>
          {/* Tabs */}
          <div style={{
            display: 'flex',
            borderBottom: `1px solid ${COLORS.border}`,
          }}>
            <button
              onClick={() => setActiveTab('notes')}
              style={{
                flex: 1,
                padding: '14px',
                border: 'none',
                backgroundColor: 'transparent',
                fontSize: '14px',
                fontWeight: activeTab === 'notes' ? 600 : 400,
                color: activeTab === 'notes' ? COLORS.primary : COLORS.textMuted,
                borderBottom: activeTab === 'notes' ? `2px solid ${COLORS.primary}` : '2px solid transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              <MessageSquare size={16} />
              الملاحظات ({notes.length})
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              style={{
                flex: 1,
                padding: '14px',
                border: 'none',
                backgroundColor: 'transparent',
                fontSize: '14px',
                fontWeight: activeTab === 'activity' ? 600 : 400,
                color: activeTab === 'activity' ? COLORS.primary : COLORS.textMuted,
                borderBottom: activeTab === 'activity' ? `2px solid ${COLORS.primary}` : '2px solid transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              <History size={16} />
              سجل النشاط ({activity.length})
            </button>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
            {activeTab === 'notes' ? (
              <>
                {/* Add Note Form */}
                <div style={{ marginBottom: '16px' }}>
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="أضف ملاحظة..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'none',
                      outline: 'none',
                      marginBottom: '8px',
                    }}
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={!newNote.trim() || addingNote}
                    data-testid="add-note-btn"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '10px 16px',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: COLORS.primary,
                      color: COLORS.white,
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: !newNote.trim() || addingNote ? 'not-allowed' : 'pointer',
                      opacity: !newNote.trim() || addingNote ? 0.6 : 1,
                    }}
                  >
                    <Send size={16} />
                    {addingNote ? 'جاري الإضافة...' : 'إضافة ملاحظة'}
                  </button>
                </div>

                {/* Notes List */}
                {notes.length === 0 ? (
                  <div style={{ padding: '30px', textAlign: 'center', color: COLORS.textMuted }}>
                    <MessageSquare size={32} style={{ marginBottom: '8px', opacity: 0.4 }} />
                    <p style={{ margin: 0, fontSize: '13px' }}>لا توجد ملاحظات</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {notes.map((note) => (
                      <div
                        key={note.note_id}
                        style={{
                          padding: '12px',
                          backgroundColor: COLORS.bg,
                          borderRadius: '8px',
                        }}
                      >
                        <p style={{ margin: '0 0 8px', fontSize: '14px', color: COLORS.text, lineHeight: 1.6 }}>
                          {note.note_text}
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: COLORS.textMuted }}>
                          <span>{note.created_by_name || note.created_by}</span>
                          <span>{formatDate(note.created_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              /* Activity Log */
              activity.length === 0 ? (
                <div style={{ padding: '30px', textAlign: 'center', color: COLORS.textMuted }}>
                  <History size={32} style={{ marginBottom: '8px', opacity: 0.4 }} />
                  <p style={{ margin: 0, fontSize: '13px' }}>لا يوجد نشاط</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {activity.map((log) => {
                    const Icon = ACTIVITY_ICONS[log.action_type] || Clock;
                    return (
                      <div key={log.log_id} style={{ display: 'flex', gap: '12px' }}>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          backgroundColor: COLORS.bgDark,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <Icon size={16} color={COLORS.textMuted} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: 0, fontSize: '13px', color: COLORS.text }}>
                            {log.action_type === 'STATUS_CHANGE' && (
                              <>تغيير الحالة من <StatusBadge status={log.from_value} size="small" /> إلى <StatusBadge status={log.to_value} size="small" /></>
                            )}
                            {log.action_type === 'CLAIMED' && 'تمت المطالبة بالطلب'}
                            {log.action_type === 'UNCLAIMED' && 'تم إعادة الطلب للمجمع'}
                            {log.action_type === 'ASSIGNED' && `تم الإسناد إلى ${log.to_value}`}
                            {log.action_type === 'NOTE_ADDED' && 'تمت إضافة ملاحظة'}
                            {log.action_type === 'CREATED' && 'تم إنشاء الطلب'}
                          </p>
                          <div style={{ display: 'flex', gap: '12px', marginTop: '4px', fontSize: '12px', color: COLORS.textMuted }}>
                            <span>{log.performed_by_name || log.performed_by}</span>
                            <span>{formatDate(log.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
